import { HTTPError } from "ky";
import { useCallback, useEffect } from "react";
import * as Yup from "yup";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
  useFetcher,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { fetchOrder } from "@/api/order";
import {
  fetchOrderItem,
  exportToNexus,
  fetchJOMCatalogueItems,
  JOMCatalogueItem,
} from "@/api/order-item";
import { Dialog } from "@/components/dialog";
import { SpinnerIcon } from "@/components/icons/spinner";
import SecondaryButton from "@/components/secondary-button";
import SubmitButton from "@/components/submit-button";
import TextField from "@/components/forms/text-field";
import SelectBox from "@/components/forms/select-box";
import { assertParameterExists } from "@/helpers/loader-guards";
import { processYupErrors } from "@/helpers/yup-errors";
import { OrderItemStatus } from "@/entities/order-item-status";
import { OrderItem } from "@/entities/order-item";
import { Order } from "@/entities/order";

const formSchema = Yup.object().shape({
  organisation_id: Yup.string().uuid("Must be a valid UUID").required("Organisation ID is required"),
  catalogue_item_type: Yup.string().required("Catalogue item type is required"),
});

interface FormValues {
  organisation_id: string;
  catalogue_item_type: string;
}

interface LoaderData {
  orderItem: OrderItem;
  order: Order;
  catalogueItems: JOMCatalogueItem[];
}

export async function loader({ params }: LoaderFunctionArgs) {
  assertParameterExists(params.orderItem);

  const [orderItem, catalogueItems] = await Promise.all([
    fetchOrderItem(params.orderItem),
    fetchJOMCatalogueItems(),
  ]);
  const order = await fetchOrder(orderItem.orderId);

  const invoice = [...order.invoices]
    .reverse()
    .find((invoice) =>
      invoice.items.find((invoiceItem) => invoiceItem.orderItemId === orderItem.id),
    );

  const invoiceSent = invoice && ["sent", "reminder_sent", "paid"].includes(invoice.status);

  if (orderItem.status !== OrderItemStatus.New || invoiceSent) {
    return redirect(`/order-items/${orderItem.id}`);
  }

  return {
    orderItem,
    order,
    catalogueItems,
  };
}

export async function action({ request, params }: ActionFunctionArgs) {
  assertParameterExists(params.orderItem);

  let input: FormValues;
  try {
    const inputEntries = Object.fromEntries((await request.formData()).entries());
    input = (await formSchema.validate(inputEntries, {
      abortEarly: false,
      stripUnknown: true,
    })) as FormValues;
  } catch (e) {
    if (e instanceof Yup.ValidationError) {
      return {
        status: 422,
        errors: processYupErrors(e),
      };
    }
    throw e;
  }

  try {
    const result = await exportToNexus({
      organisation_id: input.organisation_id,
      catalogue_item_type: input.catalogue_item_type,
      order_item_ids: [params.orderItem],
    });

    if (result.failed.length > 0) {
      return {
        status: 422,
        errors: { order_item_ids: [result.failed[0].error] },
      };
    }

    return { status: 200 };
  } catch (e) {
    if (e instanceof HTTPError && e.response.status === 422) {
      const errorJson = await e.response.json();
      return {
        status: 422,
        errors: errorJson.errors as Record<string, string[]>,
      };
    }
    return { status: 500 };
  }
}

export default function ExportToNexusDialog() {
  const { orderItem, order, catalogueItems } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const fetcher = useFetcher();

  const methods = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(formSchema) as any,
    defaultValues: {
      organisation_id: "",
      catalogue_item_type: catalogueItems[0]?.type ?? "",
    },
  });

  const close = useCallback(() => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/order-items/${orderItem.id}`);
    }
  }, [navigate, orderItem.id]);

  type ActionData = { status?: number; errors?: Record<string, string[]> } | undefined;
  const actionData = fetcher.data as ActionData;
  const isSubmitting = fetcher.state === "submitting";
  const isSuccess = actionData?.status === 200;

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        navigate(`/order-items/${orderItem.id}`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, navigate, orderItem.id]);

  return (
    <Dialog
      title="Export to AIR"
      isOpen
      onClose={close}
      description={`Export order item #${orderItem.numericId} to AIR inventory system.`}
    >
      <FormProvider {...methods}>
        <fetcher.Form
          method="post"
          noValidate
          className="flex flex-col gap-y-4"
        >
          <TextField
            name="organisation_id"
            label="Organisation ID"
            placeholder="Enter Nexus organisation UUID"
            required
            errors={actionData?.errors?.organisation_id}
          />

          <SelectBox
            name="catalogue_item_type"
            label="Catalogue Item Type"
            required
            defaultValue={catalogueItems[0]?.type}
            errors={actionData?.errors?.catalogue_item_type}
          >
            {catalogueItems.map((item) => (
              <option key={item.id} value={item.type}>
                {item.name}
              </option>
            ))}
          </SelectBox>

          <div className="mt-2 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Order Item:</strong> {orderItem.product?.title ?? "Unknown"} - {orderItem.title}
            </p>
            <p className="text-sm text-slate-600">
              <strong>Amount:</strong> {orderItem.amount.formatted}
            </p>
            <p className="text-sm text-slate-600">
              <strong>Company:</strong> {order.company?.name ?? "Unknown"}
            </p>
          </div>

          {actionData?.errors?.order_item_ids && (
            <p className="text-sm text-red-700 font-medium">
              {actionData.errors.order_item_ids[0]}
            </p>
          )}

          {actionData?.status === 500 && (
            <p className="text-sm text-red-700 font-medium">
              An unexpected error occurred. Please try again.
            </p>
          )}

          {isSuccess && (
            <p className="rounded-lg bg-green-100 text-green-800 py-3 px-4 text-center font-medium">
              Successfully exported to Nexus! Redirecting...
            </p>
          )}

          <div className="mt-4 flex items-center justify-end gap-4">
            <SecondaryButton type="button" onClick={close} size="lg" disabled={isSubmitting || isSuccess}>
              Cancel
            </SecondaryButton>
            <SubmitButton
              label="Export to AIR"
              size="lg"
              disabled={isSuccess}
              labelWhenSubmitting={<SpinnerIcon className="animate-spin h-4 w-4 text-white" />}
            />
          </div>
        </fetcher.Form>
      </FormProvider>
    </Dialog>
  );
}
